import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { ModalService } from '@app/core/services/modal.service';
import { PopupService } from '@app/core/services/popup.service';
import { StateService } from '@app/core/services/state.service';
import { UtilityService } from '@app/core/services/utility.service';
import { AgCartesianChartOptions, AgChartInstance, AgChartOptions, AgPolarChartOptions } from 'ag-charts-community';
import { DashboardChartStateMode, DashboardWidgetChart } from '../dashboard-widget-base/dashboard-widget-base.component';
import { DashboardService } from '../dashboard.service';

@Component({
    selector: 'tt-dashboard-widget-chart',
    templateUrl: './dashboard-widget-chart.component.html',
    styleUrls: ['./dashboard-widget-chart.component.css'],
})
export class DashboardWidgetChartComponent implements OnChanges, OnDestroy {
    @Input()
    public ttDashboardWidget?: DashboardWidgetChart;

    graphoption?: AgChartOptions;

    private chart?: ChartInstanceWithInternals;
    private chartElement?: HTMLElement;

    private readonly chartClickListener = async (event: MouseEvent): Promise<void> => {
        const label = this.getAxisLabel(event);

        if (!label?.state) return;

        if (label.state_mode === 'modal') {
            await this.modal.openStateDialog({ state: label.state, params: label.state_params });
        } else if (label.state_mode === 'popup') {
            await this.popup.openPopup(label.state + (label.state_params ? '/' + label.state_params : ''));
        } else if (this.utility.isModifierKeyPressed(event)) {
            this.state.newTab(label.state, label.state_params);
        } else {
            this.state.go(label.state, label.state_params);
        }
    };

    private readonly chartMouseMoveListener = (event: MouseEvent): void => {
        const chart = this.chart?.chart;

        if (!chart) return;

        const label = this.getAxisLabel(event);
        chart.ctx.cursorManager.updateCursor('dashboard-axis-label', label?.state ? 'pointer' : undefined);
    };

    private readonly chartMouseLeaveListener = (): void => {
        this.chart?.chart?.ctx.cursorManager.updateCursor('dashboard-axis-label');
    };

    constructor(
        private dashboard: DashboardService,
        private modal: ModalService,
        private popup: PopupService,
        private state: StateService,
        private utility: UtilityService,
    ) {}

    private mapGraphdata() {
        this.graphoption = {
            theme: 'ag-polychroma-dark',
            background: { visible: false },
            title: { enabled: false },
        };

        const chartType = this.getChartType();

        if (chartType === 'donut' || chartType === 'pie') {
            this.graphoption = { ...this.graphoption, ...this.dashboard.mapPolarData(chartType, this.ttDashboardWidget?.graphdata ?? []) } as AgPolarChartOptions;
        } else if (this.ttDashboardWidget?.kpicomp_type === 'GRAPH_BARH') {
            this.graphoption = { ...this.graphoption, ...this.dashboard.mapHorizontalCartesianData(this.ttDashboardWidget?.graphdata ?? []) } as AgCartesianChartOptions;
        } else {
            this.graphoption = { ...this.graphoption, ...this.dashboard.mapCartesianData(chartType, this.ttDashboardWidget?.graphdata ?? []) } as AgCartesianChartOptions;
        }
    }

    private getChartType() {
        if (this.ttDashboardWidget?.kpicomp_type === 'GRAPH_LINE') {
            return 'line';
        } else if (this.ttDashboardWidget?.kpicomp_type === 'PIE') {
            return 'pie';
        } else if (this.ttDashboardWidget?.kpicomp_type === 'DONUT') {
            return 'donut';
        } else {
            return 'bar';
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.mapGraphdata();
    }

    ngOnDestroy(): void {
        this.removeChartClickListener();
    }

    onChartReady(chart: AgChartInstance): void {
        this.removeChartClickListener();

        // AG Charts 9 renders axis labels on canvas and has no public label-click event.
        this.chart = chart as ChartInstanceWithInternals;
        this.chartElement = this.chart.chart?.element;
        this.chartElement?.addEventListener('click', this.chartClickListener);
        this.chartElement?.addEventListener('mousemove', this.chartMouseMoveListener);
        this.chartElement?.addEventListener('mouseleave', this.chartMouseLeaveListener);
    }

    private removeChartClickListener(): void {
        this.chartElement?.removeEventListener('click', this.chartClickListener);
        this.chartElement?.removeEventListener('mousemove', this.chartMouseMoveListener);
        this.chartElement?.removeEventListener('mouseleave', this.chartMouseLeaveListener);
        this.chartMouseLeaveListener();
        this.chartElement = undefined;
    }

    private getAxisLabel(event: MouseEvent): AxisLabel | undefined {
        const chart = this.chart?.chart;

        if (!chart) return;

        for (const axis of chart.axes) {
            const labelNodes = axis.tickLabelGroup?.children ?? [];
            const labelIndex = labelNodes.findIndex((node) => node.visible && node.computeTransformedBBox()?.containsPoint(event.offsetX, event.offsetY));

            if (labelIndex === -1) continue;

            const labelNode = labelNodes[labelIndex];
            const label = labelNode?.datum?.text ?? labelNode?.text;

            if (label === undefined) return;

            const axisValue = axis.tickLineGroup?.children[labelIndex]?.datum?.tick;
            const graphData = this.getGraphDataForAxisValue(axisValue);
            const state = axis.direction === 'x' ? graphData?.x_state : graphData?.y_state;

            return {
                label,
                axis: axis.direction,
                axisValue,
                state,
                state_params: axis.direction === 'x' ? graphData?.x_state_params : graphData?.y_state_params,
                state_mode: state ? (axis.direction === 'x' ? graphData?.x_state_mode : graphData?.y_state_mode) ?? 'default' : undefined,
            };
        }

        return;
    }

    private getGraphDataForAxisValue(axisValue: unknown) {
        const graphData = this.ttDashboardWidget?.graphdata ?? [];

        return graphData.find((data) => data.xkey === axisValue || data.yname === axisValue || data.yvalue === axisValue);
    }
}

interface ChartInstanceWithInternals extends AgChartInstance {
    chart?: {
        axes: AxisWithLabelGroup[];
        ctx: {
            cursorManager: {
                updateCursor(callerId: string, style?: string): void;
            };
        };
        element: HTMLElement;
    };
}

interface AxisLabel {
    label: string;
    axis: 'x' | 'y';
    axisValue: unknown;
    state?: string;
    state_params?: string;
    state_mode?: DashboardChartStateMode;
}

interface AxisWithLabelGroup {
    direction: 'x' | 'y';
    tickLabelGroup?: {
        children: AxisLabelNode[];
    };
    tickLineGroup?: {
        children: AxisTickNode[];
    };
}

interface AxisTickNode {
    datum?: {
        tick?: unknown;
    };
}

interface AxisLabelNode {
    datum?: {
        text?: string;
    };
    text?: string;
    visible: boolean;
    computeTransformedBBox():
        | {
              containsPoint(x: number, y: number): boolean;
          }
        | undefined;
}
